<template>
  <div class="creative-chat flex flex-col h-full min-h-0">
    <!-- Conversation history -->
    <div ref="history" v-loading="loading" class="border rounded border-grayNeutralLight p-2 flex-1 min-h-0 overflow-auto">
      <div v-if="!messages.length && !loading" class="h-full flex items-center justify-center text-center px-2">
        <p class="text-gray text-12px mb-0">No messages yet. Start the conversation about this creative.</p>
      </div>

      <div v-for="message in messages" :key="message.id" class="flex gap-1 mb-[12px]">
        <img
          v-if="message.user && message.user.user_settings && message.user.user_settings.company_logo"
          :src="message.user.user_settings.company_logo"
          :alt="message.user.name"
          class="rounded-full w-4 h-4 shrink-0 object-cover"
        />
        <div
          v-else
          class="rounded-full w-4 h-4 shrink-0 bg-grayNeutralLight flex items-center justify-center text-12px font-bold text-gray"
        >
          {{ initials(message) }}
        </div>

        <div class="min-w-0">
          <p class="font-bold mb-[4px]">
            {{ message.user && message.user.name }}
            <span v-if="message.user && message.user.type" class="font-normal text-gray">{{ message.user.type.title }}</span>
          </p>

          <p v-if="message.comment" class="text-12px mb-[4px] whitespace-pre-wrap break-words">{{ message.comment }}</p>

          <!-- Attachments -->
          <div v-if="message.attachments && message.attachments.length" class="flex flex-wrap gap-1 mb-[4px]">
            <a
              v-for="file in message.attachments"
              :key="file.id"
              :href="imageSignature(file.url)"
              target="_blank"
              rel="noopener noreferrer"
              class="block rounded-[6px] overflow-hidden border border-grayNeutralLight"
              :title="file.name"
            >
              <img
                v-if="file.file_type === 'image'"
                :src="imageSignature(file.thumbnail_url || file.url)"
                :alt="file.name"
                class="w-[64px] h-[64px] object-cover"
              />
              <span v-else class="w-[64px] h-[64px] flex flex-col items-center justify-center text-gray text-12px px-[2px] text-center">
                <svg width="16" height="16" fill="none" class="mb-[2px]">
                  <use xlink:href="#img-placeholder"></use>
                </svg>
                {{ file.file_type }}
              </span>
            </a>
          </div>

          <p class="text-gray text-12px mb-0">{{ message.created_at | formatDate }}</p>
        </div>
      </div>
    </div>

    <!-- Composer -->
    <div v-if="canParticipate" class="mt-1 shrink-0">
      <!-- Pending attachments -->
      <div v-if="pendingFiles.length" class="flex flex-wrap gap-1 mb-1">
        <div
          v-for="(file, index) in pendingFiles"
          :key="index"
          class="relative rounded-[6px] overflow-hidden border border-grayNeutralLight w-[48px] h-[48px]"
        >
          <img v-if="file.preview" :src="file.preview" :alt="file.name" class="w-full h-full object-cover" />
          <span v-else class="w-full h-full flex items-center justify-center text-gray text-12px">file</span>
          <button
            class="absolute top-0 right-0 bg-white/90 w-[16px] h-[16px] leading-none text-12px text-gray"
            title="Remove attachment"
            @click="removePendingFile(index)"
          >
            &times;
          </button>
        </div>
      </div>

      <el-input
        v-model="draft"
        type="textarea"
        :rows="2"
        :maxlength="500"
        show-word-limit
        resize="none"
        placeholder="Write a message…"
        @keydown.enter.exact.native.prevent="submit"
      />

      <div class="flex items-center gap-1 mt-1">
        <input ref="fileInput" type="file" :accept="acceptedTypes" multiple class="hidden" @change="handleFileSelect" />
        <ElButton type="text" class="p-0 text-gray flex items-center" :disabled="sending" @click="$refs.fileInput.click()">
          <svg width="14" height="14" fill="none" class="mr-[4px]">
            <use xlink:href="#upload-slim"></use>
          </svg>
          Attach
        </ElButton>
        <ElButton
          type="primary"
          class="ml-auto"
          size="small"
          icon="el-icon-s-promotion"
          :loading="sending"
          :disabled="!canSend"
          @click="submit"
        >
          Send
        </ElButton>
      </div>
    </div>
  </div>
</template>

<script>
const MAX_FILES = 10
const MAX_FILE_SIZE = 50 * 1024 * 1024

export default {
  name: 'CreativeChat',
  props: {
    creativeId: {
      type: null,
      default: null,
    },
    canParticipate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      messages: [],
      draft: '',
      pendingFiles: [],
      loading: false,
      sending: false,
      acceptedTypes: 'image/jpeg,image/png,image/gif,video/mp4,video/avi,video/quicktime,video/webm',
    }
  },
  computed: {
    canSend() {
      return !this.sending && (!!this.draft.trim() || this.pendingFiles.length > 0)
    },
  },
  watch: {
    creativeId: {
      immediate: true,
      handler(value) {
        if (value) this.fetchMessages()
      },
    },
  },
  beforeDestroy() {
    this.pendingFiles.forEach((file) => file.preview && URL.revokeObjectURL(file.preview))
  },
  methods: {
    imageSignature(img) {
      return this.signature(img, 'socialproducts', 1)
    },
    initials(message) {
      const name = (message.user && message.user.name) || ''
      return name
        .split(' ')
        .map((part) => part.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase()
    },
    async fetchMessages() {
      this.loading = true
      try {
        this.messages = await this.$store.dispatch('socialBoostCampaign/getCreativeChat', {
          creative_id: this.creativeId,
        })
        this.scrollToLatest()
      } catch (e) {
        console.error(e)
        // A 403 or a dead connection must not masquerade as an empty conversation.
        this.$message.error('The conversation could not be loaded.')
      } finally {
        this.loading = false
      }
    },
    handleFileSelect(event) {
      const selected = Array.from(event.target.files || [])

      // Checked once, not per file: selecting 15 files should produce one
      // "up to 10" toast, not five identical ones.
      if (this.pendingFiles.length + selected.length > MAX_FILES) {
        this.$message.error(`You can attach up to ${MAX_FILES} files per message.`)
      }

      selected.forEach((raw) => {
        if (this.pendingFiles.length >= MAX_FILES) {
          return
        }
        if (raw.size > MAX_FILE_SIZE) {
          this.$message.error(`"${raw.name}" is larger than 50MB.`)
          return
        }
        this.pendingFiles.push({
          raw,
          name: raw.name,
          size: raw.size,
          preview: raw.type.startsWith('image/') ? URL.createObjectURL(raw) : null,
        })
      })

      // allow re-selecting the same file after removing it
      event.target.value = ''
    },
    removePendingFile(index) {
      const [removed] = this.pendingFiles.splice(index, 1)
      if (removed && removed.preview) URL.revokeObjectURL(removed.preview)
    },
    async submit() {
      if (!this.canSend) return

      this.sending = true
      try {
        const response = await this.$store.dispatch('socialBoostCampaign/sendCreativeChatMessage', {
          creative_id: this.creativeId,
          message: this.draft.trim(),
          files: this.pendingFiles,
        })

        // The endpoint returns the stored message with its relations loaded —
        // append it instead of re-fetching the whole conversation.
        if (response && response.chat_message) {
          this.messages.push(response.chat_message)
          this.scrollToLatest()
        } else {
          await this.fetchMessages()
        }

        this.draft = ''
        this.pendingFiles.forEach((file) => file.preview && URL.revokeObjectURL(file.preview))
        this.pendingFiles = []
      } catch (e) {
        console.error(e)
        this.$message.error('Message could not be sent. Please try again.')
      } finally {
        this.sending = false
      }
    },
    scrollToLatest() {
      this.$nextTick(() => {
        const history = this.$refs.history
        if (history) history.scrollTop = history.scrollHeight
      })
    },
  },
}
</script>
